import React, { useEffect, useState } from 'react';
import { devCost, fmtAED } from '../utils';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../api';

const STATUS_LABELS = {
  trial: 'Trial', recipe_confirmed: 'Recipe Confirmed',
  commercial_trial: 'Commercial Trial', launched: 'Launched',
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { dashboardAPI.getSummary().then(r => setData(r.data)); }, []);

  if (!data) return <div className="empty-state"><div className="empty-state-icon">⬡</div><p>Loading...</p></div>;

  const totalCost = data.byDepartment.reduce((s, d) => s + (d.total_cost || 0), 0);
  const statusOrder = ['trial', 'recipe_confirmed', 'commercial_trial', 'launched'];

  return (
    <div>

      {/* Top stats */}
      <div className="stats-grid">
        <div className="stat-card purple" style={{ cursor: "pointer" }} onClick={() => navigate("/projects")}>
          <div className="stat-label">Total Projects</div>
          <div className="stat-value">{data.totalProjects}</div>
        </div>
        <div className="stat-card blue" style={{ cursor: "pointer" }} onClick={() => navigate("/products-list")}>
          <div className="stat-label">Total Products</div>
          <div className="stat-value">{data.totalProducts}</div>
        </div>
        <div className="stat-card amber" style={{ cursor: "pointer" }} onClick={() => navigate("/projects")}>
          <div className="stat-label">Total R&D Cost</div>
          <div className="stat-value" style={{ fontSize: 20 }}>AED {totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
          <div className="stat-sub">Trial + Market Sample + R&D</div>
        </div>
        <div className="stat-card purple" style={{ cursor: "pointer" }} onClick={() => navigate("/products-list?status=launched")}>
          <div className="stat-label">Launched</div>
          <div className="stat-value">{data.byStatus.find(s => s.status === 'launched')?.count || 0}</div>
        </div>
      </div>

      <div className="grid-2 mb-6">
        {/* Status pipeline */}
        <div className="card">
          <div className="card-title">Product Pipeline</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {statusOrder.map(s => {
              const item = data.byStatus.find(x => x.status === s);
              const count = item?.count || 0;
              const pct = data.totalProducts ? Math.round((count / data.totalProducts) * 100) : 0;
              return (
                <div key={s}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span className={`badge badge-${s}`}>{STATUS_LABELS[s]}</span>
                    <span className="mono" style={{ fontSize: 13 }}>{count} <span className="text-muted">({pct}%)</span></span>
                  </div>
                  <div style={{ height: 5, background: 'var(--bg-elevated)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
            {data.totalProducts === 0 && <div className="text-muted" style={{ fontSize: 13 }}>No products yet</div>}
          </div>
        </div>

        {/* Department breakdown */}
        <div className="card">
          <div className="card-title">By Department</div>
          {data.byDepartment.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>No data yet</div>
          ) : (
            <table style={{ width: '100%' }}>
              <thead><tr><th>Department</th><th className="text-right">Products</th><th className="text-right">Cost</th></tr></thead>
              <tbody>
                {data.byDepartment.map(d => (
                  <tr key={d.department} onClick={() => navigate(`/by-department?dept=${encodeURIComponent(d.department)}`)}>
                    <td style={{ fontWeight: 500 }}>{d.department}</td>
                    <td className="text-right mono">{d.count}</td>
                    <td className="text-right cost-value">AED {(d.total_cost || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="grid-2 mb-6">
        {/* Project costs */}
        <div className="card">
          <div className="card-title">Project Cost Breakdown</div>
          {data.projectCosts.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>No projects yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.projectCosts.map(p => {
                const maxCost = Math.max(...data.projectCosts.map(x => x.total_cost), 1);
                const pct = Math.round((p.total_cost / maxCost) * 100);
                return (
                  <div key={p.project_code}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                      <span className="cost-value mono" style={{ fontSize: 12 }}>AED {(p.total_cost || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: 'var(--blue)', borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Recent products */}
      <div className="card">
        <div className="card-title">Recent Products</div>
        {data.recentProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">◈</div>
            <p>No products yet. Start by creating a project, then add products.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th><th>Name</th><th>Project</th><th>Dept</th><th>Group</th><th>Type</th><th>Status</th><th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.recentProducts.map(p => (
                  <tr key={p.id} onClick={() => navigate(`/products/${p.id}`)}>
                    <td className="mono">{p.product_code}</td>
                    <td style={{ fontWeight: 500 }}>{p.name}</td>
                    <td className="text-muted">{p.project_name}</td>
                    <td>{p.department}</td>
                    <td className="mono">{p.internal_group}</td>
                    <td className="text-muted">{p.product_type || '—'}</td>
                    <td><span className={`badge badge-${p.status}`}>{STATUS_LABELS[p.status] || p.status}</span></td>
                    <td className="text-right cost-value">{(devCost(p)) > 0 ? `AED ${(devCost(p)).toLocaleString('en-AE', { maximumFractionDigits: 0 })}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
